import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGetProfileQuery } from "../services";
import { setUser } from "../store/slices/profile/slice";

// RTK Query hooks
import { useLoginMutation, useLogoutMutation } from "services/auth/api";

// Create the authentication context
const AuthContext = createContext();

// Create a provider component
export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginMutation] = useLoginMutation();
  const [logoutMutation] = useLogoutMutation();
  const skipInit = new URLSearchParams(window.location.search).has("skipInit");

  // Get auth state from Redux
  const { user, isAuth, isInitialize, isLoading: isAuthLoading } = useSelector((state) => state.auth);
  const prevIsAuthRef = React.useRef(isAuth);

  const { data, isLoading: isProfileLoading, isError, refetch } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: skipInit || (!isAuth && !isInitialize),
  });

  // Handle logout when profile query fails
  useEffect(() => {
    const handleLogout = async () => {
      await logoutMutation();
    };

    // Only trigger logout if we were actually authenticated before the error occurred.
    // This prevents the "double login" issue where isError from the initial 401 
    // triggers logout immediately after loginMutation succeeds but before isAuth updates locally.
    // We check if we were already auth (prevIsAuthRef.current === true) before this error.
    if (isError && isAuth && prevIsAuthRef.current) {
      handleLogout();
    }

    // Update ref AFTER the check to capture the state for the next evaluation.
    prevIsAuthRef.current = isAuth;
  }, [isError, isAuth, logoutMutation]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("skipInit")) {
      params.delete("skipInit");
      const newUrl = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // Login function that handles the login logic
  const onLogin = async (credentials) => {
    try {
      // Call the login mutation
      const result = await loginMutation(credentials).unwrap();

      // If login was successful, try to refetch the profile
      if (result?.success) {
        console.log("Login successful, refetching profile...");
        try {
          // We trigger refetch to ensure we have the latest user data.
          // The isAuth state will update via the slice (ExtraReducers).
          await refetch();
        } catch (refetchError) {
          console.warn("Failed to refetch profile after login:", refetchError);
        }
      }

      return result;
    } catch (error) {
      console.error("Login mutation error:", error);
      throw error;
    }
  };

  // Logout function
  const onLogout = async () => {
    try {
      const result = await logoutMutation();

      // Navigate to home page
      navigate("/");

      return { success: true, ...result };
    } catch (error) {
      console.error("Logout error:", error);
      return {
        success: false,
        message: error?.data?.message || "Logout failed. Please try again.",
      };
    }
  };

  // Logout due to session timeout
  const onLogoutDueToTimeout = async () => {
    try {
      await logoutMutation();
      // Navigate to login page with session expired reason
      navigate("/?reason=session_expired", { replace: true });
    } catch (error) {
      console.error("Session timeout logout error:", error);
      navigate("/?reason=session_expired", { replace: true });
    }
  };

  // Update user data
  const onUpdate = (userData) => {
    dispatch(
      setUser({
        ...user,
        ...userData,
      })
    );
  };

  // The context value that will be available to child components
  const value = {
    user: data,
    onLogin,
    onLogout,
    onLogoutDueToTimeout,
    onUpdate,
    isAuth: isAuth,
    isInitialize: isInitialize,
    isLoading: isAuthLoading || isProfileLoading,
    isAdmin: user?.role === "admin",
  };

  return (
    <AuthContext.Provider value={value}>
      {/* {!value.loading && children} */}
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
